import React from "react";
import { useFormContext } from "react-hook-form";
import type { ComponentSize } from "@common/types";
import { LabelPlacement } from "@common/types";
import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
    (props, ref) => {
        const sizeClasses = {
            xs: "input-xs",
            sm: "input-sm",
            md: "input-md",
            lg: "input-lg",
            xl: "input-xl",
        };

        const sizeClass = props.size ? sizeClasses[props.size] : "";

        // always call the hook, but it returns null if not inside FormProvider
        const formContext = useFormContext();
        const error = formContext?.formState?.errors?.[props.name];

        // use register if form context exists
        // otherwise use traditional props with the forwarded ref
        const inputProps = formContext
            ? formContext.register(props.name, { onChange: props.onChange })
            : {
                ref,
                name: props.name,
                value: props.value,
                onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
                    props.onChange?.(e),
            };

        return (
            <div className="w-full">
                <label
                    className={`input input-primary border-primary/25 inline-flex ${
                        props.fullWidth ? "w-full" : ""
                    } ${sizeClass} ${props.labelClass}`}
                    htmlFor={props.name}
                >
                    {props.labelPlacement === LabelPlacement.Inside &&
                        props.labelText && (
                        <span className="mr-2">{props.labelText}</span>
                    )}

                    {props.icon && <FontAwesomeIcon icon={props.icon} />}

                    <input
                        className={`grow input-primary input ${sizeClass} -ml-3 placeholder-gray-400 ${props.inputClass}`}
                        id={props.name}
                        type={props.type}
                        placeholder={props.placeholder}
                        disabled={props.disabled}
                        autoComplete={props.autoComplete}
                        inputMode={props.inputMode}
                        aria-label={props.ariaLabel}
                        {...inputProps}
                    />
                </label>

                {props.labelPlacement === LabelPlacement.After &&
                    props.labelText && (
                    <p className="label">{props.labelText}</p>
                )}

                {!props.hideError && !!error && (
                    <p className="text-red-500 text-sm mt-1">
                        {error?.message as string}
                    </p>
                )}
            </div>
        );
    },
);

export interface InputProps {
    name: string;
    value?: string;
    inputClass?: string;
    labelClass?: string;
    type?: string;
    placeholder?: string;
    icon?: IconProp;
    size?: ComponentSize;
    fullWidth?: boolean;
    labelPlacement?: LabelPlacement;
    labelText?: string;
    disabled?: boolean;
    hideError?: boolean;
    // Pass "off" for fields that must never enter browser form-history or a password
    // manager's autofill store (bank routing/account numbers on a shared B2B workstation).
    autoComplete?: string;
    // Digit keypad on mobile without making the field type="number" (which strips leading
    // zeros — fatal for a routing number).
    inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
    // Accessible name for a field with no visible label (a search box named only by its placeholder).
    ariaLabel?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}
